import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, 
  Check, 
  Shield, 
  Users, 
  Calculator,
  Calendar,
  Bell,
  FileText,
  TrendingUp,
  Link2,
  CheckCircle2,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BrandedHomeLink } from '@/components/BrandedHomeLink';
import { LegalSiteLinks } from '@/components/LegalSiteLinks';
import { SquareDealLink } from '@/components/SquareDealLink';
import {
  buildPlanMarketingBullets,
  formatIncludedSeats,
  isPlanComingSoon,
  planComingSoonSubtitle,
  planListAnnualPrice,
  planListMonthlyPrice,
  planSeatPriceMonthly,
} from '@/utils/planMarketing';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

function LandingPlanCard({ plan, plans, navigate }) {
  const isComingSoon = isPlanComingSoon(plan);
  const isPro = plan.tier_id === 'pro' && !isComingSoon;
  const isTeam = plan.tier_id === 'team' && !isComingSoon;
  const isFree = plan.tier_id === 'free';
  const bullets = buildPlanMarketingBullets(plans, plan);
  const checkIconClass = isComingSoon ? 'text-muted-foreground' : 'text-primary';
  const pm = planListMonthlyPrice(plan);
  const pa = planListAnnualPrice(plan);
  const includedSeats = formatIncludedSeats(plan);
  const extraSeat = planSeatPriceMonthly(plan);
  const savePct = pm > 0 && pa > 0 ? Math.round((1 - pa / (pm * 12)) * 100) : 0;

  let subtitle = '';
  if (isComingSoon) {
    subtitle = planComingSoonSubtitle(plan);
  }

  return (
    <div
      className={`border rounded-2xl p-6 relative flex flex-col ${
        isPro
          ? 'bg-gradient-to-b from-primary/10 to-transparent border-2 border-primary/50 transform lg:scale-105'
          : isTeam
            ? 'bg-gradient-to-b from-blue-500/10 to-transparent border-2 border-blue-500/40'
            : 'bg-muted/50 border-border'
      }`}
    >
      {isComingSoon && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-amber-500 text-foreground text-sm font-semibold px-4 py-1 rounded-full">
          Coming Soon
        </div>
      )}
      {isPro && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-sm font-semibold px-4 py-1 rounded-full">
          Most Popular
        </div>
      )}
      {isTeam && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-sm font-semibold px-4 py-1 rounded-full">
          For your team
        </div>
      )}

      <div className={`text-lg font-semibold mb-2 ${isPro ? 'text-primary' : isTeam ? 'text-blue-400' : isComingSoon ? 'text-amber-400' : isFree ? 'text-muted-foreground' : 'text-blue-400'}`}>
        {plan.name || plan.tier_id}
      </div>

      {isComingSoon ? (
        <>
          <div className="text-2xl font-bold text-foreground/90 mb-1">Custom</div>
          <div className="text-muted-foreground mb-4 text-sm">{subtitle}</div>
        </>
      ) : (
        <>
          <div className="text-4xl font-bold text-foreground mb-1">
            {pm > 0 ? `$${Math.round(pm)}` : '$0'}
          </div>
          <div className="text-foreground/90 mb-1">
            {pm > 0 ? '/month' : 'forever'}
          </div>
          {includedSeats && extraSeat != null ? (
            <div className="text-xs text-foreground/80 mb-1">
              for {includedSeats} people, then +${Math.round(extraSeat)} for each extra person
            </div>
          ) : null}
          {!isFree && pa > 0 && pm > 0 && (
            <div className="text-xs text-foreground/80 mb-4">
              or ${Math.round(pa)}/year
              {savePct > 0 ? ` (save ${savePct}%)` : ''}
            </div>
          )}
          {(isFree || (pa <= 0 && !(includedSeats && extraSeat != null))) && <div className="mb-4" />}
          {includedSeats && extraSeat != null && pa <= 0 ? <div className="mb-3" /> : null}
        </>
      )}

      {plan.description ? (
        <p className="text-xs text-muted-foreground leading-snug mb-3">{plan.description}</p>
      ) : null}

      {!isComingSoon && (
        <div className="space-y-1.5 text-xs text-muted-foreground mb-4 border-t border-border/60 pt-3">
          <div className="flex justify-between gap-2">
            <span>Deals</span>
            <span className="text-foreground font-medium text-right shrink-0">
              {plan.max_transactions == null ? 'Unlimited' : plan.max_transactions}
            </span>
          </div>
          <div className="flex justify-between gap-2">
            <span>People</span>
            <span className="text-foreground font-medium text-right shrink-0">
              {includedSeats
                ? `${includedSeats} included`
                : plan.max_team_members == null || plan.max_team_members >= 999999
                  ? 'Unlimited'
                  : plan.max_team_members}
            </span>
          </div>
          <div className="flex justify-between gap-2">
            <span>Saved deal setups</span>
            <span className="text-foreground font-medium text-right shrink-0">
              {plan.max_templates == null ? 'Unlimited' : plan.max_templates}
            </span>
          </div>
        </div>
      )}

      <ul className="space-y-2.5 mb-8 text-sm flex-1">
        {bullets.map((line, idx) => (
          <li key={`${plan.tier_id}-${idx}-${line.slice(0, 24)}`} className="flex items-start gap-2 text-foreground/90">
            <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${checkIconClass}`} />
            {line.startsWith('Transaction Coordinator') ? (
              <strong className="text-primary">{line}</strong>
            ) : (
              line
            )}
          </li>
        ))}
      </ul>

      {isComingSoon ? (
        <Button className="w-full bg-muted/50 text-foreground/90 cursor-not-allowed mt-auto" disabled>
          Coming Soon
        </Button>
      ) : (
        <Button
          className={`w-full mt-auto ${
            isFree
              ? 'bg-muted hover:bg-muted'
              : isPro
                ? 'bg-primary hover:bg-primary/85'
                : 'bg-blue-600 hover:bg-blue-500'
          }`}
          onClick={() => navigate('/login?signup=true')}
        >
          {isFree ? 'Start My Free Account' : `Start ${plan.name || plan.tier_id}`}
        </Button>
      )}
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [catalogPlans, setCatalogPlans] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!API_BASE) {
      setCatalogError('Missing REACT_APP_BACKEND_URL');
      setCatalogLoading(false);
      return () => { cancelled = true; };
    }
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/catalog/subscription-plans`);
        if (!res.ok) throw new Error('Could not load plans');
        const data = await res.json();
        if (!cancelled) setCatalogPlans(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setCatalogError(e.message || 'error');
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const id = (window.location.hash || '').replace(/^#/, '');
    if (!id) return;
    const timer = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView();
    }, 50);
    return () => window.clearTimeout(timer);
  }, []);

  const freeTxCount = catalogPlans.find((p) => p.tier_id === 'free')?.max_transactions;
  const liveTeam = catalogPlans.find((p) => p.tier_id === 'team' && !isPlanComingSoon(p));
  const teamMonthly = liveTeam ? planListMonthlyPrice(liveTeam) : 0;
  const teamSeats = liveTeam ? formatIncludedSeats(liveTeam) : null;

  const goSignup = () => navigate('/login?signup=true');

  const steps = [
    {
      icon: Calendar,
      title: 'Open a deal',
      description: 'Pick a setup and add the dates you care about. Weekends and Hawaii holidays are already skipped.',
    },
    {
      icon: Bell,
      title: 'Get reminded in time',
      description: 'An alert shows up in the app and in your email before something is due.',
    },
    {
      icon: Users,
      title: 'Send your client a link',
      description: 'They see the same due dates you do. They do not see the rest of the file.',
    },
  ];

  const extras = [
    {
      icon: FileText,
      title: (
        <>
          Fill the <SquareDealLink /> form
        </>
      ),
      description: 'Type the answers once and make a PDF. You still check it against what your clients agreed to.',
    },
    {
      icon: Calculator,
      title: 'Hawaii tax math, no signup',
      description: 'Conveyance tax, HARPTA, FIRPTA, and closing estimates. Try the tools first if you are not ready for an account.',
    },
    {
      icon: TrendingUp,
      title: 'Bring people you work with',
      description: 'Invite your team when you need a shared view. Five people are included on Team. Each extra person is billed at the Pro price.',
    },
  ];

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <BrandedHomeLink to="/dashboard" logoClassName="w-8 h-8">
              <span className="font-bold text-xl">TerraKronos</span>
            </BrandedHomeLink>
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('how-it-works')} className="text-muted-foreground hover:text-foreground transition-colors">
                How it works
              </button>
              <button onClick={() => navigate('/learn')} className="text-muted-foreground hover:text-foreground transition-colors">
                Tutorials
              </button>
              <button onClick={() => navigate('/tools')} className="text-muted-foreground hover:text-foreground transition-colors">
                Hawaii Calculators
              </button>
              <button onClick={() => navigate('/faq')} className="text-muted-foreground hover:text-foreground transition-colors">
                FAQ
              </button>
              <button onClick={() => scrollToSection('pricing')} className="text-muted-foreground hover:text-foreground transition-colors">
                Pricing
              </button>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
                className="text-foreground/90 hover:text-foreground"
              >
                Sign In
              </Button>
              <Button 
                onClick={goSignup}
                className="bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-bold text-base px-8 py-6 h-auto"
              >
                Start Free
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero: outcome, who it is for, how it works, one CTA, risk reversal */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
        <div className="absolute top-40 right-1/4 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-muted/50 border border-border rounded-full mb-8 animate-fade-in">
              <Shield className="w-4 h-4 text-primary" />
              <span className="text-sm text-foreground/90">For Hawaii real estate agents</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              <span className="text-foreground">Never miss a </span>
              <span className="bg-gradient-to-r from-primary to-primary/90 bg-clip-text text-transparent">Hawaii contract deadline</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              TerraKronos counts weekends and Hawaii holidays for you, reminds you
              before something is due, and lets your client see the same dates.
            </p>
            
            <div className="flex flex-col items-center justify-center gap-4 mb-8">
              <Button 
                size="lg"
                onClick={goSignup}
                data-testid="landing-hero-cta"
                className="w-full max-w-xl bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-bold text-2xl sm:text-3xl px-10 py-8 sm:py-10 h-auto min-h-[5.5rem] rounded-2xl shadow-2xl shadow-primary/30 group [&_svg]:size-8"
              >
                Start My Free Account
                <ArrowRight className="ml-3 w-8 h-8 group-hover:translate-x-1 transition-transform" />
              </Button>
              <p className="text-sm text-foreground/90">
                No credit card. Sign in with Google or email. Cancel anytime.
              </p>
              <button
                type="button"
                onClick={() => navigate('/faq')}
                className="text-sm text-primary hover:underline"
              >
                Questions? Read the FAQ
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Problem, then the fix, then the same CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                <span className="text-red-400">A missed date can</span>
                <br />
                <span className="text-foreground">cost you the deal</span>
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p className="flex items-start gap-3">
                  <span className="text-red-400 mt-1">✗</span>
                  You count days in a spreadsheet and hope you skipped the right holidays
                </p>
                <p className="flex items-start gap-3">
                  <span className="text-red-400 mt-1">✗</span>
                  The client asks &quot;when is this due?&quot; and you hunt through email
                </p>
                <p className="flex items-start gap-3">
                  <span className="text-red-400 mt-1">✗</span>
                  Mainland tools do not know Kamehameha Day from a normal Monday
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-muted to-card rounded-2xl p-8 border border-border">
              <h3 className="text-2xl font-bold text-primary mb-6">What you get instead</h3>
              <div className="space-y-4">
                <p className="flex items-start gap-3 text-foreground">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  Due dates skip weekends and Hawaii holidays for you
                </p>
                <p className="flex items-start gap-3 text-foreground">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  You get a reminder before the date, not after
                </p>
                <p className="flex items-start gap-3 text-foreground">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  Your client can open the same dates from a link
                </p>
              </div>
            </div>
          </div>
          <div className="flex justify-center mt-12">
            <Button
              size="lg"
              onClick={goSignup}
              className="bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-bold text-lg px-10 py-6 h-auto"
            >
              Start My Free Account
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* How it works: process, not a second feature dump */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-card/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              How it works
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Three steps. No new vocabulary.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, idx) => (
              <div 
                key={idx}
                className="group p-6 bg-muted/50 border border-border rounded-xl hover:border-primary/50 hover:bg-muted transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold flex items-center justify-center">
                    {idx + 1}
                  </div>
                  <div className="w-12 h-12 bg-primary/10 border border-primary/20 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <step.icon className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">{step.title}</h3>
                <p className="text-muted-foreground">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-muted/50 border border-border rounded-full mb-6">
            <Link2 className="w-4 h-4 text-primary" />
            <span className="text-sm text-foreground/90">Dates that wait on each other</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            The next deadline can start when the last job is done
          </h2>
          <p className="text-xl text-muted-foreground">
            Mark a task done, and the dates that depend on it can start that day.
            Finish early, and the next deadline moves up with you.
          </p>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Also in the app
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              The dates are the point. These are here when you need them.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {extras.map((feature, idx) => (
              <div 
                key={idx}
                className="group p-6 bg-muted/50 border border-border rounded-xl hover:border-primary/50 hover:bg-muted transition-all duration-300"
              >
                <div className="w-12 h-12 bg-primary/10 border border-primary/20 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Secondary path: tax tools without an account */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="relative bg-gradient-to-r from-primary/10 via-purple-500/10 to-primary/10 rounded-3xl p-8 sm:p-12 border border-primary/20 overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 rounded-full blur-3xl" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-4">
                <Calculator className="w-6 h-6 text-primary" />
                <span className="text-primary font-semibold">No account needed</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Not ready to sign up?
              </h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl">
                Use the Hawaii tax tools now. Conveyance tax, HARPTA, FIRPTA, and closing estimates.
              </p>
              <Button 
                size="lg"
                onClick={() => navigate('/tools')}
                className="bg-background text-foreground hover:bg-muted font-semibold text-lg px-8 border border-border"
              >
                Try Hawaii Calculators
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Start free. <span className="text-primary">Pay when you need more.</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              No credit card. Keep the free plan as long as you want.
              {liveTeam && teamMonthly > 0
                ? ` Team is $${Math.round(teamMonthly)}/month${teamSeats ? ` for ${teamSeats} people` : ''}, then the Pro price for each extra person.`
                : ''}
            </p>
          </div>
          
          {catalogLoading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="w-10 h-10 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading current plans...</p>
            </div>
          )}

          {catalogError && !catalogLoading && (
            <p className="text-center text-muted-foreground max-w-md mx-auto">
              We could not load prices right now. Sign in and open Settings, then Subscription, or try again later.
            </p>
          )}

          {!catalogLoading && !catalogError && catalogPlans.length > 0 && (
            <div className="grid gap-6 max-w-7xl mx-auto grid-cols-1 sm:grid-cols-2 xl:grid-cols-[repeat(auto-fit,minmax(220px,1fr))]">
              {catalogPlans.map((plan) => (
                <LandingPlanCard key={plan.tier_id || plan.name} plan={plan} plans={catalogPlans} navigate={navigate} />
              ))}
            </div>
          )}
          
          <div className="mt-12 text-center">
            <p className="text-foreground/90 text-sm">
              Every plan counts Hawaii holidays on your dates, and includes the free tax tools.
              {' '}
              Yearly billing saves money on Basic and Pro.
            </p>
            <p className="text-sm text-muted-foreground mt-3">
              Questions about plan limits, security, or SquareDeal?{' '}
              <button
                type="button"
                onClick={() => navigate('/faq')}
                className="text-primary hover:underline"
              >
                Read the FAQ
              </button>
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-card to-background">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            Open one deal. <span className="text-primary">See if the dates stay straight.</span>
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            {freeTxCount != null
              ? `${freeTxCount} free deals. `
              : 'Free to start. '}
            No credit card. Sign in with Google or email.
          </p>
          <div className="flex flex-col items-center justify-center gap-4">
            <Button 
              size="lg"
              onClick={goSignup}
              data-testid="landing-final-cta"
              className="w-full max-w-xl bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-bold text-2xl sm:text-3xl px-10 py-8 sm:py-10 h-auto min-h-[5.5rem] rounded-2xl shadow-2xl shadow-primary/30 group [&_svg]:size-8"
            >
              Start My Free Account
              <ArrowRight className="ml-3 w-8 h-8 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
          <p className="mt-6 text-sm text-foreground/90">
            Cancel anytime
          </p>
        </div>
      </section>

      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <BrandedHomeLink to="/dashboard" logoClassName="w-8 h-8">
              <span className="font-bold text-xl">TerraKronos</span>
            </BrandedHomeLink>
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-sm text-muted-foreground">
              <button onClick={() => navigate('/learn')} className="hover:text-foreground transition-colors">
                Tutorials
              </button>
              <button onClick={() => navigate('/tools')} className="hover:text-foreground transition-colors">
                Hawaii Calculators
              </button>
              <button onClick={() => navigate('/faq')} className="hover:text-foreground transition-colors">
                FAQ
              </button>
              <LegalSiteLinks linkClassName="hover:text-foreground transition-colors" />
              <span>© 2026 TerraKronos. Built for Hawaii.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
